import { useContext, useEffect, useMemo, useRef, useState } from 'react';

import { AutoCompleteDomains, AutoCompleteResult, ID } from 'src/models';
import { useAutocomplete } from 'src/hooks/useAutocomplete';
import { mockFn } from 'src/tools/fn.tools';
import { InputContext } from 'src/contexts/input.context';
import { BaseComponentProps } from 'src/components/Dropdown';
import { DropdownMenu } from 'src/components/Dropdown/DropdownMenu';
import { InputWithIcons } from 'src/components/Inputs';
import { ifTrue } from 'src/tools/logic.tools';

export interface CSIAutoCompleteInputProps extends BaseComponentProps {
  onChange: (value: string) => void;
  onSubmit: () => void;
  onDiscard: () => void;
  isLoading?: boolean;
  value: string;
}

interface Props {
  renderInput?: (props: CSIAutoCompleteInputProps) => JSX.Element;
}

export function CSIAutoComplete({ renderInput }: Props) {
  const {
    value,
    data: description,
    onChange,
    classNames,
    onSubmit = mockFn,
    onDiscard = mockFn,
    isDisabled,
    isAutoFocus,
    onClick,
    placeholder,
    customization = {},
  } = useContext(InputContext);

  const isUserChangeRef = useRef(false);

  const { dropdownMinWidth = 'container-width' } = customization;

  const [isOpen, setIsOpen] = useState(false);

  const initialValue = useMemo(() => {
    if (!value) return '';

    return description ? `${value} ${description}` : value;
  }, [description, value]);

  const [inputValue, setInputValue] = useState(initialValue);

  const { results, isLoading, onChangeHandler } = useAutocomplete(AutoCompleteDomains.CSI_CODES);

  useEffect(() => {
    if (!isUserChangeRef.current) setInputValue(initialValue);

    isUserChangeRef.current = false;
  }, [initialValue]);

  function handleOnItemClick(id: ID) {
    const item = results.find(i => i.id === id);

    setIsOpen(false);
    setInputValue(item ? renderItemLabel(item) : id);

    onChange(String(id), true);

    isUserChangeRef.current = true;
  }

  function getHandleOnClick(fn: () => void) {
    return function call() {
      fn();

      if (onClick) onClick();
    };
  }

  function handleOnChange(userInput: string) {
    setIsOpen(true);
    setInputValue(userInput);

    onChangeHandler(userInput);
    onChange(userInput || undefined);

    isUserChangeRef.current = true;
  }

  function handleOnRequestOpenClose(state: boolean) {
    setIsOpen(state);

    if (!state) onDiscard();
  }

  function handleOnDiscard() {
    setIsOpen(false);

    onDiscard();
  }

  function handleOnSubmit() {
    setIsOpen(false);

    onSubmit();
  }

  function renderItemLabel({ id, label }: AutoCompleteResult) {
    return `${id} ${label}`;
  }

  function renderBaseComponent(baseComponentProps: BaseComponentProps) {
    const extendedProps = {
      value: inputValue,
      onChange: handleOnChange,
      onDiscard: handleOnDiscard,
      onSubmit: handleOnSubmit,
      onClick: getHandleOnClick(baseComponentProps.onClick),
      isLoading,
    };

    if (renderInput)
      return renderInput({
        ...baseComponentProps,
        ...extendedProps,
      });

    const iconId = baseComponentProps.isOpen ? 'chevron-up' : 'chevron-down';

    return (
      <InputWithIcons
        {...extendedProps}
        className={classNames?.input}
        onFocus={baseComponentProps.onFocus}
        onBlur={baseComponentProps.onBlur}
        isDisabled={isDisabled}
        autoFocus={isAutoFocus}
        rightIconId={ifTrue(results.length, iconId)}
        placeholder={placeholder}
      />
    );
  }

  return (
    <DropdownMenu
      classNames={classNames}
      items={results}
      onItemClick={handleOnItemClick}
      renderBaseComponent={renderBaseComponent}
      dropdownMinWidth={dropdownMinWidth}
      isOpen={isOpen && !isLoading}
      onRequestOpenClose={handleOnRequestOpenClose}
      renderItemLabel={renderItemLabel}
    />
  );
}
